import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useAuth } from '../../auth/AuthContext';
import { useTranslation } from 'react-i18next';
// No need to import Material-UI icons since we're using custom SVG icons

export default function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { t } = useTranslation();
  
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const scrollDifference = currentScrollY - lastScrollY;
          
          // Show navigation when scrolling up, hide when scrolling down
          if (scrollDifference > 5 && currentScrollY > 50) {
            // Scrolling down more than 5px and past 50px - hide navigation
            setIsVisible(false);
          } else if (scrollDifference < -5) {
            // Scrolling up more than 5px - show navigation
            setIsVisible(true);
          }
          
          setLastScrollY(currentScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true }); 
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  // Don't render on desktop
  if (!isMobile) {
    return null;
  }

  const handleNavigation = (path: string) => {
    if (path === '#') return; // Placeholder paths
    
    if (path === '/profile') {
      // Check if user is authenticated before navigating to profile
      if (!user) {
        navigate('/sign-in');
        return;
      }
      navigate('/profile');
    } else if (path === '/wishlist') {
      // Check if user is authenticated before navigating to wishlist
      if (!user) {
        navigate('/sign-in');
        return;
      }
      navigate('/wishlist');
    } else if (path === 'categories') {
      // Open side menu for categories
      const event = new CustomEvent('openSideMenu');
      window.dispatchEvent(event);
    } else {
      navigate(path);
    }
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        transform: isVisible ? 'translateY(0)' : 'translateY(100vh)',
        transition: 'transform 0.3s ease-in-out',
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
      elevation={8}
    >
      <BottomNavigation
        value={location.pathname}
        showLabels={true}
        onChange={(_, newValue) => {
          handleNavigation(newValue);
        }}
        sx={{
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 0',
            '&.Mui-selected': {
              color: theme.palette.custom?.brown || '#b8957b',
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.75rem',
            marginTop: '4px',
          },
        }}
      >
        <BottomNavigationAction
          label={t('navigation.home')}
          value="/"
          icon={<img 
            src={location.pathname === "/" ? "/images/ic_tab_home_select.svg" : "/images/ic_tab_home_unselect.svg"} 
            alt="Home" 
            style={{ width: 24, height: 24 }} 
          />}
          onClick={() => handleNavigation('/')}
        />
        <BottomNavigationAction
          label={t('navigation.categories')}
          value="categories"
          icon={<img 
            src={location.pathname === "categories" ? "/images/ic_tab_categories_select.svg" : "/images/ic_tab_categories_unselect.svg"} 
            alt="Categories" 
            style={{ width: 24, height: 24 }} 
          />}
          onClick={() => handleNavigation('categories')}
        />
        <BottomNavigationAction
          label={t('navigation.brands')}
          value="/brands"
          icon={<img 
            src={location.pathname === "/brands" ? "/images/ic_tab_brands_select.svg" : "/images/ic_tab_brands_unselect.svg"} 
            alt="Brands" 
            style={{ width: 24, height: 24 }} 
          />}
          onClick={() => handleNavigation('/brands')}
        />
        <BottomNavigationAction
          label={t('navigation.wishlist')}
          value="/wishlist"
          icon={<img 
            src={location.pathname === "/wishlist" ? "/images/ic_tab_wishlist_select.svg" : "/images/ic_tab_wishlist_unselect.svg"} 
            alt="Wishlist" 
            style={{ width: 24, height: 24 }} 
          />}
          onClick={() => handleNavigation('/wishlist')}
        />
        <BottomNavigationAction
          label={t('navigation.account')}
          value="/profile"
          icon={<img 
            src={location.pathname === "/profile" ? "/images/ic_tab_account_select.svg" : "/images/ic_tab_account_unselect.svg"} 
            alt="Account" 
            style={{ width: 24, height: 24 }} 
          />}
          onClick={() => handleNavigation('/profile')}
        />
      </BottomNavigation>
    </Paper>
  );
}
